import React from 'react';
import { Navigate } from 'react-router-dom';
import { Button, Grid, IconButton, makeStyles } from '@material-ui/core';
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
  EntityAboutCard,
  EntityLayout,
  EntityLinksCard,
  EntitySwitch,
  EntityOrphanWarning,
  EntityProcessingErrorsPanel,
  isComponentType,
  isKind,
  hasCatalogProcessingErrors,
  isOrphan,
  hasRelationWarnings,
  EntityRelationWarning,
  EntityDependsOnComponentsCard,
  EntityDependsOnResourcesCard,
  EntityHasComponentsCard,
  EntityHasSubcomponentsCard
} from '@backstage/plugin-catalog';
import {
  EntityUserProfileCard,
  EntityGroupProfileCard,
  EntityOwnershipCard,
  EntityMembersListCard,
} from '@backstage/plugin-org';
import { EntityTechdocsContent } from '@backstage/plugin-techdocs';
import { EmptyState, TabbedLayout } from '@backstage/core-components';
import { Direction, EntityCatalogGraphCard } from '@backstage/plugin-catalog-graph';
import { 
  EntityProvidedApisCard, 
  EntityConsumedApisCard, 
  EntityHasApisCard  // Corrected import statement
} from '@backstage/plugin-api-docs';
import EntityFullApiDetailsCard from './EntityFullApiDetailsCard';
import { EntityHasResourcesCard } from '@backstage/plugin-catalog';
import { EntityHasSystemsCard } from '@backstage/plugin-catalog';

import {
  RELATION_PART_OF,
  RELATION_HAS_PART,
  RELATION_API_CONSUMED_BY,
  RELATION_API_PROVIDED_BY,
  RELATION_CONSUMES_API,
  RELATION_PROVIDES_API,
  RELATION_DEPENDENCY_OF,
  RELATION_DEPENDS_ON,
} from '@backstage/catalog-model';
import {EntityNodeRenderer} from "./EntityNodeRenderer";
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../redux/hooks';
import { selectCatalogTableState } from '../../redux/catalogTableSlice';
import { CustomEntityHeader } from './CustomEntityHeader';
import CustomEirTable from './CustomEirTable';
import { CustomEntityAboutCard } from './CustomEntityAboutCard';
import { ApiDefinitionPage } from './ApiDefinitionPage';

const useStyles = makeStyles({
  aboutCard: {
    '& h6': {
      fontWeight: 600,
    },
    '& p': {
      fontSize: '1.1rem',
    },
    // Keep the single header divider very thin and subtle.
    '& hr': {
      backgroundColor: 'rgba(0, 0, 0, 0.08)',
    },
  },
});

 
const customEntityFilterKind = ['Component', 'API', 'System', 'Resource'];

const techdocsContent = (
  <EntityTechdocsContent />
);

const cicdContent = (
  <EntitySwitch>
    <EntitySwitch.Case>
      <EmptyState
        title="No CI/CD available for this entity"
        missing="info"
        description="You need to add an annotation to your component if you want to enable CI/CD for it. You can read more about annotations in Backstage by clicking the button below."
        action={
          <Button
            variant="contained"
            color="primary"
            href="https://backstage.io/docs/features/software-catalog/well-known-annotations"
          >
            Read more
          </Button>
        }
      />
    </EntitySwitch.Case>
  </EntitySwitch>
);

const entityWarningContent = (
  <>
    <EntitySwitch>
      <EntitySwitch.Case if={isOrphan}>
        <Grid item xs={12}>
          <EntityOrphanWarning />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>
    <EntitySwitch>
      <EntitySwitch.Case if={hasRelationWarnings}>
        <Grid item xs={12}>
          <EntityRelationWarning />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>
    <EntitySwitch>
      <EntitySwitch.Case if={hasCatalogProcessingErrors}>
        <Grid item xs={12}>
          <EntityProcessingErrorsPanel />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>
  </>
);

const overviewContent = (
  <Grid container spacing={3}  direction='column'>
    {entityWarningContent}
    <Grid item> 
      <EntityAboutCard />
    </Grid>
    <Grid item>
      <EntityCatalogGraphCard variant="gridItem" height={400}  renderNode={EntityNodeRenderer}/>
    </Grid>
    <Grid item>
      <EntityLinksCard />
    </Grid>
    <Grid item>
      <EntityHasSubcomponentsCard variant="gridItem" />
    </Grid>
  </Grid>
);

const serviceEntityPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      {overviewContent}
    </EntityLayout.Route>
    <EntityLayout.Route path="/ci-cd" title="CI/CD">
      {cicdContent}
    </EntityLayout.Route>
    <EntityLayout.Route path="/api" title="API">
      <Grid container spacing={3} alignItems="stretch">
        <Grid item md={6}>
          <EntityProvidedApisCard />
        </Grid>
        <Grid item md={6}>
          <EntityConsumedApisCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>
    <EntityLayout.Route path="/dependencies" title="Dependencies">
      <Grid container spacing={3} alignItems="stretch">
        <Grid item md={6}>
          <EntityDependsOnComponentsCard variant="gridItem" />
        </Grid>
        <Grid item md={6}>
          <EntityDependsOnResourcesCard variant="gridItem" />
        </Grid>
      </Grid>
    </EntityLayout.Route>
    <EntityLayout.Route path="/docs" title="Docs">
      {techdocsContent}
    </EntityLayout.Route>
  </EntityLayout>
);

const websiteEntityPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      {overviewContent}
    </EntityLayout.Route>
    <EntityLayout.Route path="/ci-cd" title="CI/CD">
      {cicdContent}
    </EntityLayout.Route>
    <EntityLayout.Route path="/dependencies" title="Dependencies">
      <Grid container spacing={3} alignItems="stretch">
        <Grid item md={6}>
          <EntityDependsOnComponentsCard variant="gridItem" />
        </Grid>
        <Grid item md={6}>
          <EntityDependsOnResourcesCard variant="gridItem" />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </EntityLayout>
);

const defaultEntityPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      {overviewContent}
    </EntityLayout.Route>
    <EntityLayout.Route path="/docs" title="Docs">
      {techdocsContent}
    </EntityLayout.Route>
  </EntityLayout>
);

const componentPage = (
  <EntitySwitch>
    <EntitySwitch.Case if={isComponentType('service')}>
      {serviceEntityPage}
    </EntitySwitch.Case>
    <EntitySwitch.Case if={isComponentType('website')}>
      {websiteEntityPage}
    </EntitySwitch.Case>
    <EntitySwitch.Case>{defaultEntityPage}</EntitySwitch.Case>
  </EntitySwitch>
);


const ApiPage  = () => {
  const navigate = useNavigate();
  const classes = useStyles(); 
  const savedURL = useAppSelector(selectCatalogTableState).savedUrl;
  
  const handleRedirectMainPage = () => {
    if (savedURL) {
        navigate(savedURL)
    } else {
        console.log("Error in Saving URL")
        navigate('/catalog?limit=500&filters%5Bkind%5D=API');
    }
    // const storedURL = localStorage.getItem("savedURL");
    // if (storedURL) {
    //     navigate(storedURL)
    // } else {
    //     console.warn("No saved URL found!");
    // }
};
  return (
    <>
    <CustomEntityHeader />
      <TabbedLayout>
        <TabbedLayout.Route path="/" title="Overview">
          <IconButton onClick={handleRedirectMainPage} color="primary" style={{
            borderRadius: "40%",
            width: "40px",
            height: "40px",
            margin: "8px",
            marginLeft: "-2px",
            backgroundColor: "white",
            color: "black",
          }}>
            <ArrowBackIcon />
          </IconButton>
          <Grid container spacing={3} direction='column'>
            {entityWarningContent}
            <Grid item className={classes.aboutCard}>
              <CustomEntityAboutCard />
            </Grid>
            <Grid item className="relations-graph-box">
              <EntityCatalogGraphCard variant="gridItem" height={300} renderNode={EntityNodeRenderer} />
            </Grid>
            <Grid container item md={12}>
              {/* <Grid item md={6}>
            <EntityProvidingComponentsCard />
          </Grid>
          <Grid item md={6}>
            <EntityConsumingComponentsCard />
          </Grid> */}
              <Grid item md={12} xs={12}>
                <EntityFullApiDetailsCard />
              </Grid>
            </Grid>
          </Grid>
        </TabbedLayout.Route>
        <TabbedLayout.Route path="/swaggerView" title="OpenAPI Specification">
          <ApiDefinitionPage />
        </TabbedLayout.Route>
        <TabbedLayout.Route path="/eir" title="EIR">
          {/* <IconButton onClick={handleRedirectMainPage} color="primary" style={{
            borderRadius: "40%",
            width: "40px",
            height: "40px",
            margin: "8px",
            marginLeft: "-2px",
            backgroundColor: "white",
            color: "black",
          }}>
            <ArrowBackIcon />
          </IconButton> */}
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <CustomEirTable />
            </Grid>
          </Grid>
        </TabbedLayout.Route>
      </TabbedLayout>
    </>
  );
};
export default ApiPage;

const userPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3}>
        {entityWarningContent}
        <Grid item xs={12} md={6}>
          <EntityUserProfileCard variant="gridItem" />
        </Grid>
        <Grid item xs={12} md={6}>
          <EntityOwnershipCard variant="gridItem" />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </EntityLayout>
);

const groupPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3}>
        {entityWarningContent}
        <Grid item xs={12} md={6}>
          <EntityGroupProfileCard variant="gridItem" />
        </Grid>
        <Grid item xs={12} md={6}>
          <EntityOwnershipCard variant="gridItem" entityFilterKind={customEntityFilterKind}/>
        </Grid>
        <Grid item xs={12} md={6}>
          <EntityMembersListCard />
        </Grid>
        <Grid item xs={12} md={6}>
          <EntityLinksCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </EntityLayout>
);

const systemPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3}  direction='column'>
        {entityWarningContent}
        <Grid item>
          <EntityAboutCard variant="gridItem" />
        </Grid>
        <Grid item>
          <EntityCatalogGraphCard variant="gridItem" height={900}  renderNode={EntityNodeRenderer}/>
        </Grid>
        <Grid item>
          <EntityLinksCard />
        </Grid>
        <Grid item>
          <EntityHasComponentsCard variant="gridItem" />
        </Grid>
        <Grid item>
          <EntityHasApisCard variant="gridItem" />
        </Grid>
        <Grid item>
          <EntityHasResourcesCard variant="gridItem" />
        </Grid>
      </Grid>
    </EntityLayout.Route>
    <EntityLayout.Route path="/diagram" title="Diagram">
      <EntityCatalogGraphCard
        variant="gridItem"
        direction={Direction.TOP_BOTTOM}
        title="System Diagram"
        height={700}
        relations={[
          RELATION_PART_OF,
          RELATION_HAS_PART,
          RELATION_API_CONSUMED_BY,
          RELATION_API_PROVIDED_BY,
          RELATION_CONSUMES_API,
          RELATION_PROVIDES_API,
          RELATION_DEPENDENCY_OF,
          RELATION_DEPENDS_ON,
        ]}
        unidirectional={true}
        renderNode={EntityNodeRenderer}
      />
    </EntityLayout.Route>
  </EntityLayout>
);

const domainPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3}  direction='column'>
        {entityWarningContent}
        <Grid item>
          <EntityAboutCard variant="gridItem" />
        </Grid>
        <Grid item>
          <EntityCatalogGraphCard variant="gridItem" height={400}  renderNode={EntityNodeRenderer}/>
        </Grid>
        <Grid item>
          <EntityHasSystemsCard variant="gridItem" />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </EntityLayout>
);

export const entityPage = (
  <EntitySwitch>
    <EntitySwitch.Case if={isKind('component')} children={componentPage} />
    <EntitySwitch.Case if={isKind('api')} children={<ApiPage/>} />
    <EntitySwitch.Case if={isKind('group')} children={groupPage} />
    <EntitySwitch.Case if={isKind('user')} children={userPage} />
    <EntitySwitch.Case if={isKind('system')} children={systemPage} />
    <EntitySwitch.Case if={isKind('domain')} children={domainPage} />
    <EntitySwitch.Case>{defaultEntityPage}</EntitySwitch.Case>
  </EntitySwitch>
);